import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { GUIA_LABELS } from '@utils/guiaLabels.js'
import { searchGlobal, SEARCH_KIND_LABEL } from '@hooks/useGlobalSearch.js'

interface Props {
  open: boolean
  onClose: () => void
}

export function GlobalSearch({ open, onClose }: Props) {
  const navigate = useNavigate()
  const inputRef = useRef<HTMLInputElement>(null)
  const [query, setQuery] = useState('')
  const results = searchGlobal(query)

  useEffect(() => {
    if (open) {
      setQuery('')
      requestAnimationFrame(() => inputRef.current?.focus())
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  const onSelect = useCallback(
    (path: string) => {
      navigate(path)
      onClose()
    },
    [navigate, onClose],
  )

  if (!open) return null

  const dialog = (
    <div className='cf3-global-search' role='dialog' aria-modal aria-label='Busca global'>
      <button
        type='button'
        className='cf3-global-search__backdrop'
        onClick={onClose}
        aria-label='Fechar'
      />
      <div className='cf3-global-search__panel'>
        <div className='cf3-global-search__head'>
          <input
            ref={inputRef}
            type='search'
            className='cf3-global-search__input'
            placeholder={GUIA_LABELS.searchPlaceholder}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label='Busca global'
            autoComplete='off'
          />
          <button
            type='button'
            className='cf3-global-search__close'
            onClick={onClose}
            aria-label='Fechar busca'
          >
            ✕
          </button>
        </div>
        {query.trim().length >= 2 && (
          <ul className='cf3-global-search__results' role='listbox'>
            {results.map((hit) => (
              <li key={hit.id}>
                <button
                  type='button'
                  className='cf3-global-search__hit'
                  onClick={() => onSelect(hit.path)}
                >
                  <span className='cf3-global-search__hit-kind'>{SEARCH_KIND_LABEL[hit.kind]}</span>
                  <span className='cf3-global-search__hit-body'>
                    <span className='cf3-global-search__hit-title'>{hit.title}</span>
                    {hit.meta && <span className='cf3-global-search__hit-meta'>{hit.meta}</span>}
                  </span>
                </button>
              </li>
            ))}
            {results.length === 0 && (
              <li className='cf3-global-search__empty'>
                Nenhum resultado para &quot;{query}&quot;
              </li>
            )}
          </ul>
        )}
      </div>
    </div>
  )

  const shell = typeof document !== 'undefined' ? document.getElementById('app') : null
  return shell ? createPortal(dialog, shell) : dialog
}
